import { useState, useEffect } from 'react';
import { useAuthFetch } from './useAuthFetch';
import { useAdminContext } from '../contexts/AdminContextProvider';

export interface EffectiveSchema {
  tenant_id: string;
  template_slug: string | null;
  template_version: string | null;
  navigation: {
    groups: TemplateNavGroup[];
    defaultTab: string;
    showPlatformAdmin?: boolean;
  };
  features: string[];               // template feature_defaults
  tenant_features: string[];        // actual tenant.features from DB (WQ-076)
  tenant_tier: string | null;       // tenant.tier from DB (WQ-076)
  ai_config: Record<string, unknown>;
  branding: Record<string, unknown>;
  view_registry: Record<string, unknown>;
  stub_metadata: Record<string, StubMetadata>;
}

export interface TemplateNavGroup {
  id: string;
  label: string;
  icon?: string;
  defaultExpanded?: boolean;
  requiredRoles?: string[];         // role gating (WQ-TENANT-ADMIN-NAV)
  requiredFeature?: string;         // feature flag gating (WQ-076)
  requiredPlan?: string[];          // plan tier gating (WQ-076)
  items: TemplateNavItem[];
}

export interface TemplateNavItem {
  id: string;
  label: string;
  icon?: string;
  view?: string;  // view registry key, defaults to 'stub'
  requiredRoles?: string[];         // WQ-217: item-level role gating (mirrors group-level)
  requiredFeature?: string;         // item-level feature gating (WQ-076)
  requiredPlan?: string[];          // item-level plan gating (WQ-076)
}

export interface StubMetadata {
  title: string;
  description: string;
  icon?: string;
  expectedPhase?: string;
}

interface UseEffectiveSchemaResult {
  schema: EffectiveSchema | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetch the effective schema for a tenant.
 *
 * Uses the admin endpoint when tenantId is provided,
 * otherwise uses the tenant-portal endpoint for the current user's tenant.
 *
 * Gracefully degrades: 404 or network error -> null schema -> platform nav fallback.
 */
export function useEffectiveSchema(
  tenantId?: string,
  isAdmin: boolean = false
): UseEffectiveSchemaResult {
  const { authFetch, isCancelled } = useAuthFetch();
  const { schemaVersion } = useAdminContext();
  const [schema, setSchema] = useState<EffectiveSchema | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function fetchSchema() {
      // Admin without tenant context → skip fetch, use platform nav fallback
      // Non-admin without explicit tenantId → try tenant-portal endpoint (uses JWT tenant_id)
      if (isAdmin && !tenantId) {
        setSchema(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const endpoint = isAdmin && tenantId
          ? `/api/admin/tenants/${tenantId}/effective-schema`
          : '/api/tenant-portal/effective-schema';

        const response = await authFetch(endpoint, {
          requestKey: 'effective-schema',
        });

        if (isCancelled(response)) return;

        if (!response.ok) {
          // Template system not available yet (WQ-050 may not be deployed),
          // tenant has no template, no tenant context, or insufficient
          // permissions for the endpoint — not an error, graceful fallback
          if (response.status === 400 || response.status === 403 || response.status === 404 || response.status === 501) {
            if (active) {
              setSchema(null);
              setIsLoading(false);
            }
            return;
          }
          throw new Error(`Failed to fetch schema: ${response.status}`);
        }

        const data = await response.json();
        if (active) {
          setSchema(data);
        }
      } catch (err) {
        if (active) {
          // Network errors are expected when the endpoint doesn't exist yet
          setError(err instanceof Error ? err.message : 'Unknown error');
          setSchema(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    fetchSchema();
    return () => { active = false; };
  }, [tenantId, isAdmin, authFetch, isCancelled, schemaVersion]);

  return { schema, isLoading, error };
}
